/**
 * Release Details API Service
 *
 * Handles communication with the backend for release notes CRUD and pagination.
 */

import { API_BASE, getAuthHeaders, fetchApi } from './apiClient';

export interface ReleaseDetail {
  id: number;
  release_number: string;
  description: string;
  release_type: string;
  files_changed: string[] | null;
  author: string | null;
  tags: string[] | null;
  created_at: string | null;
}

export interface ReleaseListResponse {
  releases: ReleaseDetail[];
  total: number;
  limit: number;
  offset: number;
}

export interface ReleaseDetailCreate {
  release_number: string;
  description: string;
  release_type?: string;
  files_changed?: string[];
  author?: string;
  tags?: string[];
}

class ReleaseApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE}/releases`;
  }

  /**
   * List releases with pagination
   */
  async getReleases(params: {
    release_type?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<ReleaseListResponse> {
    const searchParams = new URLSearchParams();
    if (params.release_type) searchParams.set('release_type', params.release_type);
    if (params.limit !== undefined) searchParams.set('limit', params.limit.toString());
    if (params.offset !== undefined) searchParams.set('offset', params.offset.toString());

    const url = `${this.baseUrl}${searchParams.toString() ? `?${searchParams}` : ''}`;
    const headers = await getAuthHeaders();
    const response = await fetchApi(url, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get releases: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get a single release by ID
   */
  async getRelease(releaseId: number): Promise<ReleaseDetail> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/${releaseId}`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get release: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create a new release
   */
  async createRelease(data: ReleaseDetailCreate): Promise<ReleaseDetail> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(this.baseUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to create release: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a release
   */
  async deleteRelease(releaseId: number): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/${releaseId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete release: ${response.statusText}`);
    }
  }
}

export const releaseApi = new ReleaseApi();
